<script setup>
/**
 * OCHAT: cargar una reserva a mano.
 *
 * El caso de uso es concreto: entró un llamado, o alguien escribió por un canal que
 * el agente IA no atiende, y el equipo del hotel necesita cargarla.
 *
 * EL FLUJO ES BUSCAR PRIMERO, ELEGIR DESPUÉS
 * No hay un formulario con un desplegable de tipos de habitación, porque eso
 * permitiría elegir algo que no se puede vender y enterarse al guardar. Acá se
 * consultan fechas y ocupación, se muestran las ofertas REALES con su precio, y se
 * reserva una de ellas. La disponibilidad es la que manda.
 *
 * Los rechazos se muestran igual que las ofertas, con su motivo, para que el equipo
 * pueda decirle al huésped "esa fecha pide mínimo 3 noches" sin adivinar.
 */
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const uiFlags = useMapGetter('bookings/getUIFlags');

const todayISO = () => new Date().toLocaleDateString('en-CA');
const addDays = (iso, count) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d) + count * 86400000)
    .toISOString()
    .slice(0, 10);
};

const form = ref({
  checkin: todayISO(),
  checkout: addDays(todayISO(), 1),
  adults: 2,
  children: 0,
  guestName: '',
  guestPhone: '',
  guestEmail: '',
  notes: '',
});

const result = ref(null);
const searched = ref(false);

const nights = computed(() => {
  const { checkin, checkout } = form.value;
  if (!checkin || !checkout) return 0;
  const [ay, am, ad] = checkin.split('-').map(Number);
  const [by, bm, bd] = checkout.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
});

const rangeIsValid = computed(() => nights.value > 0);

const search = async () => {
  searched.value = false;
  result.value = await store.dispatch('bookings/checkAvailability', {
    checkin: form.value.checkin,
    checkout: form.value.checkout,
    adults: form.value.adults,
    children: form.value.children,
  });
  searched.value = true;
};

const money = (amount, currency) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currency || 'ARS',
  }).format(Number(amount || 0));

const book = async offer => {
  try {
    const booking = await store.dispatch('bookings/create', {
      room_type_id: offer.room_type_id,
      rate_plan_id: offer.rate_plan_id,
      checkin: form.value.checkin,
      checkout: form.value.checkout,
      adults: form.value.adults,
      children: form.value.children,
      source: 'dashboard',
      guest: {
        name: form.value.guestName,
        phone: form.value.guestPhone,
        email: form.value.guestEmail,
        notes: form.value.notes,
      },
    });

    useAlert(t('BOOKINGS.NEW.CREATED', { code: booking.code }));
    router.push(accountScopedRoute('bookings_list'));
  } catch (error) {
    // La API devuelve el motivo; mostrarlo es la diferencia entre "no se pudo" y
    // saber qué cambiar.
    const reason = error.response?.data?.reason;
    useAlert(
      reason
        ? t(`BOOKINGS.REASON.${String(reason).toUpperCase()}`)
        : t('BOOKINGS.NEW.FAILED')
    );
  }
};
</script>

<template>
  <div class="flex flex-col max-w-4xl gap-6">
    <section class="flex flex-col gap-3">
      <h2 class="text-heading-2 text-n-slate-12">
        {{ $t('BOOKINGS.NEW.SEARCH_TITLE') }}
      </h2>

      <div class="flex flex-wrap items-end gap-3">
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.NEW.CHECKIN') }}
          <input
            v-model="form.checkin"
            type="date"
            class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.NEW.CHECKOUT') }}
          <input
            v-model="form.checkout"
            type="date"
            class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.NEW.ADULTS') }}
          <input
            v-model.number="form.adults"
            type="number"
            min="1"
            class="w-20 px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.NEW.CHILDREN') }}
          <input
            v-model.number="form.children"
            type="number"
            min="0"
            class="w-20 px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          />
        </label>
        <button
          type="button"
          :disabled="!rangeIsValid || uiFlags.isCheckingAvailability"
          class="px-4 py-1.5 text-sm font-medium rounded-lg bg-n-brand text-white disabled:opacity-50"
          @click="search"
        >
          {{ $t('BOOKINGS.NEW.SEARCH') }}
        </button>
      </div>

      <p v-if="!rangeIsValid" class="text-sm text-n-ruby-11">
        {{ $t('BOOKINGS.NEW.INVALID_RANGE') }}
      </p>
      <p v-else class="text-sm text-n-slate-11">
        {{ $t('BOOKINGS.LIST.NIGHTS', { n: nights }) }}
      </p>
    </section>

    <section v-if="searched" class="flex flex-col gap-3">
      <h2 class="text-heading-2 text-n-slate-12">
        {{ $t('BOOKINGS.NEW.GUEST_TITLE') }}
      </h2>
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          v-model="form.guestName"
          type="text"
          class="px-3 py-1.5 text-sm rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          :placeholder="$t('BOOKINGS.NEW.GUEST_NAME')"
        />
        <input
          v-model="form.guestPhone"
          type="tel"
          class="px-3 py-1.5 text-sm rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          :placeholder="$t('BOOKINGS.NEW.GUEST_PHONE')"
        />
        <input
          v-model="form.guestEmail"
          type="email"
          class="px-3 py-1.5 text-sm rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          :placeholder="$t('BOOKINGS.NEW.GUEST_EMAIL')"
        />
        <input
          v-model="form.notes"
          type="text"
          class="px-3 py-1.5 text-sm rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          :placeholder="$t('BOOKINGS.NEW.NOTES')"
        />
      </div>
    </section>

    <section v-if="searched" class="flex flex-col gap-3">
      <h2 class="text-heading-2 text-n-slate-12">
        {{ $t('BOOKINGS.NEW.OFFERS_TITLE') }}
      </h2>

      <p v-if="!result?.offers?.length" class="text-sm text-n-slate-11">
        {{ $t('BOOKINGS.NEW.NO_OFFERS') }}
      </p>

      <div
        v-for="offer in result?.offers ?? []"
        :key="`${offer.room_type_id}-${offer.rate_plan_id}`"
        class="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg outline outline-1 outline-n-container"
      >
        <div class="flex flex-col gap-0.5">
          <span class="font-medium text-n-slate-12">{{ offer.room_type_name }}</span>
          <span class="text-sm text-n-slate-11">
            {{ offer.rate_plan_name }} ·
            {{ $t(offer.refundable ? 'BOOKINGS.REFUNDABLE' : 'BOOKINGS.NON_REFUNDABLE') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ $t('BOOKINGS.NEW.BOOKABLE', { n: offer.bookable }) }}
          </span>
        </div>
        <div class="flex items-center gap-4">
          <span class="font-medium text-n-slate-12">
            {{ money(offer.total, offer.currency) }}
          </span>
          <button
            type="button"
            :disabled="uiFlags.isCreating"
            class="px-4 py-1.5 text-sm font-medium rounded-lg bg-n-brand text-white disabled:opacity-50"
            @click="book(offer)"
          >
            {{ $t('BOOKINGS.NEW.BOOK') }}
          </button>
        </div>
      </div>

      <!-- Los rechazos con su motivo: es lo que permite responderle al huésped. -->
      <div
        v-for="(rejection, index) in result?.unavailable ?? []"
        :key="`x-${index}`"
        class="flex items-center justify-between gap-3 px-4 py-2 text-sm rounded-lg bg-n-slate-2"
      >
        <span class="text-n-slate-11">{{ rejection.room_type_name || '—' }}</span>
        <span class="text-n-slate-11">
          {{ $t(`BOOKINGS.REASON.${String(rejection.reason).toUpperCase()}`) }}
          <template v-if="rejection.detail">({{ rejection.detail }})</template>
        </span>
      </div>
    </section>
  </div>
</template>
